import { action, computed, observable, runInAction } from 'mobx';

import { Address, LocationType } from './address';
import type { Order } from './order';
import {
  calculateCustomerCredit,
  calculateCustomerCreditIncludingGst,
  calculateProductRestockingFee,
  calculateTotalRestockingFee,
} from './credit-request-calculator';
import { round } from '../math';

export enum CreditRequestStatus {
  Draft = 'Draft',
  AwaitingGeneralManager = 'AwaitingGeneralManager',
  Returned = 'Returned',
  AwaitingAccountsReceivable = 'AwaitingAccountsReceivable',
  Complete = 'Complete',
  Rejected = 'Rejected',
  Closed = 'Closed',
}

export enum CreditRequestFreightChargeMode {
  None = 'None',
  Deduct = 'Deduct',
  Separate = 'Separate',
}

export enum CreditRequestAuditAction {
  Created = 'Created',
  Saved = 'Saved',
  Submitted = 'Submitted',
  Resubmitted = 'Resubmitted',
  Returned = 'Returned',
  SentToAccountsReceivable = 'SentToAccountsReceivable',
  Rejected = 'Rejected',
  Closed = 'Closed',
  Completed = 'Completed',
}

export enum CreditRequestLocationType {
  None = 'None',
  Abc = 'Abc',
  Supplier = 'Supplier',
}

export enum CreditRequestTransition {
  Submit = 'Submit',
  Resubmit = 'Resubmit',
  Return = 'Return',
  SendToAccountsReceivable = 'SendToAccountsReceivable',
  Reject = 'Reject',
  Close = 'Close',
  Complete = 'Complete',
}

export interface ICreditRequestAddress {
  exWorksId?: number;
  formattedAddress: string;
  name: string;
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  latLng: { lat: number | null; lng: number | null };
  locationType: CreditRequestLocationType;
}

export interface ICreditRequestProductLineSaveRequest {
  sourceOrderProductItemId: number;
  returnQuantity: number;
  restockingFeeApplied: boolean;
  restockingFeePercentage: number;
}

export interface ICreditRequestSaveRequest {
  rowVersion: string | null;
  reason: string;
  customerCreditRequired: boolean | null;
  returnRequired: boolean;
  pickupAddress: ICreditRequestAddress | null;
  destinationAddress: ICreditRequestAddress | null;
  freightRequired: boolean | null;
  freightChargeMode: CreditRequestFreightChargeMode | null;
  freightAmount: number;
  freightMojoeNumber: string;
  noFreightReason: string;
  newPurchaseOrderRequired: boolean | null;
  newPurchaseOrderNoCharge: boolean | null;
  supplierContacted: boolean | null;
  supplierName: string;
  supplierContactName: string;
  supplierCreditRequired: boolean | null;
  supplierCreditAmount: number;
  supplierRestockingFeeRequired: boolean | null;
  supplierRestockingFeeAmount: number;
  fixedAmountCreditExGst: number;
  generalComment: string;
  cashCustomerAccountCheckedVerbally: boolean | null;
  cashCustomerAccountCheckedBy: string;
  productLines: ICreditRequestProductLineSaveRequest[];
}

export interface ICreditRequestProductLineResponse extends ICreditRequestProductLineSaveRequest {
  lineNumber: number;
  code: string;
  description: string;
  purchaseUnit: string;
  availableQuantity: number;
  unitPrice: number;
  purchaseOrderNumber: string;
}

export interface ICreditRequestCalculation {
  productReturnValue: number;
  customerRestockingFee: number;
  supplierRestockingFee: number;
  deductedFreight: number;
  calculatedCreditExGst: number;
  totalCreditExGst: number;
  totalCreditIncludingGst: number;
  hasInvalidReturnQuantity: boolean;
  hasNegativeCashCreditWarning: boolean;
  hasCreditExceedingPaidWarning: boolean;
}

export interface ICreditRequestAuditResponse {
  action: CreditRequestAuditAction;
  eventDateTimeUtc: string;
  actorRepCode: string;
  actorName: string;
  comment: string;
  payload: object;
}

export interface ICreditRequestDetailResponse {
  id: number;
  orderId: number;
  status: CreditRequestStatus;
  requesterName: string;
  requesterRepCode: string;
  requesterEmail: string;
  createdDateTimeUtc: string;
  lastModifiedDateTimeUtc: string;
  rowVersion: string;
  orderNumber: string;
  customerCode: string;
  customerName: string;
  deliveredAddress: string;
  deliveredDate: string | null;
  isCashCustomer: boolean;
  amountPaid: number;
  form: ICreditRequestSaveRequest;
  productLines: ICreditRequestProductLineResponse[];
  calculation: ICreditRequestCalculation;
  auditEntries: ICreditRequestAuditResponse[];
  canEdit: boolean;
  permittedActions: CreditRequestTransition[];
}

export interface ICreditRequestTransitionResponse {
  request: ICreditRequestDetailResponse;
  notificationQueued: boolean;
}

export class CreditRequestProductLineModel {
  @observable public sourceOrderProductItemId: number;
  @observable public lineNumber: number;
  @observable public code: string;
  @observable public description: string;
  @observable public purchaseUnit: string;
  @observable public availableQuantity: number;
  @observable public unitPrice: number;
  @observable public purchaseOrderNumber: string;
  @observable public returnQuantity: number;
  @observable public restockingFeeApplied: boolean;
  @observable public restockingFeePercentage: number;
  @observable public errors: Record<string, string> = {};

  constructor(line: ICreditRequestProductLineSaveRequest & Partial<ICreditRequestProductLineResponse>) {
    runInAction(() => {
      this.sourceOrderProductItemId = line.sourceOrderProductItemId;
      this.lineNumber = line.lineNumber || 0;
      this.code = line.code || '';
      this.description = line.description || '';
      this.purchaseUnit = line.purchaseUnit || '';
      this.availableQuantity = line.availableQuantity || 0;
      this.unitPrice = line.unitPrice || 0;
      this.purchaseOrderNumber = line.purchaseOrderNumber || '';
      this.returnQuantity = line.returnQuantity;
      this.restockingFeeApplied = line.restockingFeeApplied;
      this.restockingFeePercentage = line.restockingFeePercentage;
    });
  }

  public toRequest(): ICreditRequestProductLineSaveRequest {
    return {
      sourceOrderProductItemId: this.sourceOrderProductItemId,
      returnQuantity: this.returnQuantity,
      restockingFeeApplied: this.restockingFeeApplied,
      restockingFeePercentage: this.restockingFeePercentage,
    };
  }
}

const emptyCalculation = (): ICreditRequestCalculation => ({
  productReturnValue: 0,
  customerRestockingFee: 0,
  supplierRestockingFee: 0,
  deductedFreight: 0,
  calculatedCreditExGst: 0,
  totalCreditExGst: 0,
  totalCreditIncludingGst: 0,
  hasInvalidReturnQuantity: false,
  hasNegativeCashCreditWarning: false,
  hasCreditExceedingPaidWarning: false,
});

export const toCreditRequestLocationType = (
  value: LocationType | CreditRequestLocationType,
): CreditRequestLocationType => {
  if (typeof value === 'string') return value;
  if (value === LocationType.Abc) return CreditRequestLocationType.Abc;
  if (value === LocationType.Supplier) return CreditRequestLocationType.Supplier;
  return CreditRequestLocationType.None;
};

export const toAddressLocationType = (value: CreditRequestLocationType): LocationType => {
  if (value === CreditRequestLocationType.Abc) return LocationType.Abc;
  if (value === CreditRequestLocationType.Supplier) return LocationType.Supplier;
  return LocationType.None;
};

const copyAddress = (address?: Address | ICreditRequestAddress | null): ICreditRequestAddress | null => {
  if (!address) return null;
  return {
    exWorksId: address.exWorksId,
    formattedAddress: address.formattedAddress || '',
    name: address.name || '',
    street: address.street || '',
    suburb: address.suburb || '',
    state: address.state || '',
    postcode: address.postcode || '',
    latLng: { ...address.latLng },
    locationType: toCreditRequestLocationType(address.locationType),
  };
};

export class CreditRequestModel {
  @observable public id: number | null = null;
  @observable public orderId: number = 0;
  @observable public status: CreditRequestStatus = CreditRequestStatus.Draft;
  @observable public requesterName: string = '';
  @observable public requesterRepCode: string = '';
  @observable public requesterEmail: string = '';
  @observable public createdDateTimeUtc: string = '';
  @observable public lastModifiedDateTimeUtc: string = '';
  @observable public rowVersion: string | null = null;
  @observable public orderNumber: string = '';
  @observable public customerCode: string = '';
  @observable public customerName: string = '';
  @observable public deliveredAddress: string = '';
  @observable public deliveredDate: string | null = null;
  @observable public isCashCustomer: boolean = false;
  @observable public amountPaid: number = 0;
  @observable public reason: string = '';
  @observable public customerCreditRequired: boolean | null = null;
  @observable public returnRequired: boolean | null = null;
  @observable public pickupAddress: ICreditRequestAddress | null = null;
  @observable public destinationAddress: ICreditRequestAddress | null = null;
  @observable public freightRequired: boolean | null = null;
  @observable public freightChargeMode: CreditRequestFreightChargeMode | null = null;
  @observable public freightAmount: number = 0;
  @observable public freightMojoeNumber: string = '';
  @observable public noFreightReason: string = '';
  @observable public newPurchaseOrderRequired: boolean | null = null;
  @observable public newPurchaseOrderNoCharge: boolean | null = null;
  @observable public supplierContacted: boolean | null = null;
  @observable public supplierName: string = '';
  @observable public supplierContactName: string = '';
  @observable public supplierCreditRequired: boolean | null = null;
  @observable public supplierCreditAmount: number = 0;
  @observable public supplierRestockingFeeRequired: boolean | null = null;
  @observable public supplierRestockingFeeAmount: number = 0;
  @observable public fixedAmountCreditExGst: number = 0;
  @observable public generalComment: string = '';
  @observable public cashCustomerAccountCheckedVerbally: boolean | null = null;
  @observable public cashCustomerAccountCheckedBy: string = '';
  @observable public productLines: CreditRequestProductLineModel[] = [];
  @observable public calculation: ICreditRequestCalculation = emptyCalculation();
  @observable public auditEntries: ICreditRequestAuditResponse[] = [];
  @observable public errors: Record<string, string> = {};
  @observable private serverCanEdit: boolean = true;
  @observable private permittedActions: CreditRequestTransition[] = [];
  @observable private dirty: boolean = false;

  @computed
  public get isDirty(): boolean {
    return this.dirty;
  }

  @computed
  public get canEdit(): boolean {
    return this.serverCanEdit;
  }

  @computed
  public get availableActions(): CreditRequestTransition[] {
    return this.permittedActions.slice();
  }

  public static fromResponse(response: ICreditRequestDetailResponse): CreditRequestModel {
    const model = new CreditRequestModel();
    model.markSaved(response);
    return model;
  }

  public static forOrder(order: Order): CreditRequestModel {
    const model = new CreditRequestModel();
    runInAction(() => {
      model.orderId = order.id;
      model.orderNumber = order.orderNumber;
      model.customerCode = order.customerCode;
      model.customerName = order.customerName;
      model.deliveredAddress = order.delivery.address.formattedAddress;
      model.deliveredDate = order.delivery.date ? order.delivery.date.toString() : null;
      model.isCashCustomer = order.isCashSalesAccount;
      model.amountPaid = order.actualPaid || 0;
      model.pickupAddress = copyAddress(order.delivery.address);
      model.destinationAddress = null;
      model.productLines = order.orderedProductLines.map(line => new CreditRequestProductLineModel({
        sourceOrderProductItemId: line.id,
        lineNumber: line.lineNumber,
        code: line.code,
        description: line.name,
        purchaseUnit: line.purchaseUnit,
        availableQuantity: line.quantity,
        unitPrice: line.price,
        purchaseOrderNumber: line.purchaseOrderNumber,
        returnQuantity: 0,
        restockingFeeApplied: false,
        restockingFeePercentage: 30,
      }));
      model.serverCanEdit = true;
      model.dirty = false;
    });
    return model;
  }

  @action
  public update<K extends keyof CreditRequestModel>(property: K, value: CreditRequestModel[K]): void {
    Reflect.set(this, property, value);
    const errors = { ...this.errors };
    delete errors[String(property)];
    delete errors.totalCreditIncludingGst;
    this.errors = errors;
    this.dirty = true;
  }

  @action
  public updateProductLine(
    sourceOrderProductItemId: number,
    property: 'returnQuantity' | 'restockingFeeApplied' | 'restockingFeePercentage',
    value: number | boolean,
  ): void {
    const line = this.productLines.find(x => x.sourceOrderProductItemId === sourceOrderProductItemId);
    if (!line) return;
    if (property === 'returnQuantity' && typeof value === 'number') line.returnQuantity = value;
    if (property === 'restockingFeeApplied' && typeof value === 'boolean') {
      line.restockingFeeApplied = value;
    }
    if (property === 'restockingFeePercentage' && typeof value === 'number') {
      line.restockingFeePercentage = value;
    }
    const errors = { ...line.errors };
    delete errors[property];
    line.errors = errors;
    const requestErrors = { ...this.errors };
    delete requestErrors.totalCreditIncludingGst;
    this.errors = requestErrors;
    this.dirty = true;
  }

  @action
  public markSaved(response: ICreditRequestDetailResponse): void {
    const form = response.form;
    this.id = response.id;
    this.orderId = response.orderId;
    this.status = response.status;
    this.requesterName = response.requesterName;
    this.requesterRepCode = response.requesterRepCode;
    this.requesterEmail = response.requesterEmail;
    this.createdDateTimeUtc = response.createdDateTimeUtc;
    this.lastModifiedDateTimeUtc = response.lastModifiedDateTimeUtc;
    this.rowVersion = response.rowVersion;
    this.orderNumber = response.orderNumber;
    this.customerCode = response.customerCode;
    this.customerName = response.customerName;
    this.deliveredAddress = response.deliveredAddress;
    this.deliveredDate = response.deliveredDate;
    this.isCashCustomer = response.isCashCustomer;
    this.amountPaid = response.amountPaid;
    this.reason = form.reason || '';
    this.customerCreditRequired = form.customerCreditRequired;
    this.returnRequired = form.returnRequired;
    this.pickupAddress = copyAddress(form.pickupAddress);
    this.destinationAddress = copyAddress(form.destinationAddress);
    this.freightRequired = form.freightRequired;
    this.freightChargeMode = form.freightChargeMode;
    this.freightAmount = form.freightAmount;
    this.freightMojoeNumber = form.freightMojoeNumber || '';
    this.noFreightReason = form.noFreightReason || '';
    this.newPurchaseOrderRequired = form.newPurchaseOrderRequired;
    this.newPurchaseOrderNoCharge = form.newPurchaseOrderNoCharge;
    this.supplierContacted = form.supplierContacted;
    this.supplierName = form.supplierName || '';
    this.supplierContactName = form.supplierContactName || '';
    this.supplierCreditRequired = form.supplierCreditRequired;
    this.supplierCreditAmount = form.supplierCreditAmount;
    this.supplierRestockingFeeRequired = form.supplierRestockingFeeRequired;
    this.supplierRestockingFeeAmount = form.supplierRestockingFeeAmount;
    this.fixedAmountCreditExGst = form.fixedAmountCreditExGst;
    this.generalComment = form.generalComment || '';
    this.cashCustomerAccountCheckedVerbally = form.cashCustomerAccountCheckedVerbally;
    this.cashCustomerAccountCheckedBy = form.cashCustomerAccountCheckedBy || '';
    this.productLines = response.productLines.map(line => new CreditRequestProductLineModel(line));
    this.calculation = response.calculation;
    this.auditEntries = response.auditEntries;
    this.serverCanEdit = response.canEdit;
    this.permittedActions = response.permittedActions;
    this.errors = {};
    this.dirty = false;
  }

  @action
  public validateForReview(): boolean {
    const errors: Record<string, string> = {};
    const requiredAnswer = (value: boolean | null, key: string) => {
      if (value === null) errors[key] = 'Select Yes or No.';
    };
    const requiredText = (value: string | undefined, key: string, message: string) => {
      if (!value || !value.trim()) errors[key] = message;
    };

    requiredAnswer(this.customerCreditRequired, 'customerCreditRequired');
    requiredAnswer(this.freightRequired, 'freightRequired');
    requiredAnswer(this.supplierCreditRequired, 'supplierCreditRequired');
    requiredAnswer(this.supplierRestockingFeeRequired, 'supplierRestockingFeeRequired');
    requiredAnswer(this.supplierContacted, 'supplierContacted');
    requiredAnswer(this.newPurchaseOrderRequired, 'newPurchaseOrderRequired');

    if (this.isCashCustomer) {
      requiredAnswer(
        this.cashCustomerAccountCheckedVerbally,
        'cashCustomerAccountCheckedVerbally',
      );
      requiredText(
        this.cashCustomerAccountCheckedBy,
        'cashCustomerAccountCheckedBy',
        'Checked by is required.',
      );
    }

    let hasInvalidReturnQuantity = false;
    this.productLines.forEach(line => {
      const lineErrors: Record<string, string> = {};
      if (line.returnQuantity < 0 || line.returnQuantity > line.availableQuantity) {
        lineErrors.returnQuantity = `Must be less than ${line.availableQuantity}`;
        hasInvalidReturnQuantity = true;
      }
      if (line.restockingFeePercentage < 0 || line.restockingFeePercentage > 100) {
        lineErrors.restockingFeePercentage = 'Must be between 0 and 100';
      }
      line.errors = lineErrors;
    });

    if (this.freightAmount < 0) errors.freightAmount = 'Freight amount cannot be negative.';
    if (this.supplierCreditAmount < 0) {
      errors.supplierCreditAmount = 'Supplier credit amount cannot be negative.';
    }
    if (this.supplierRestockingFeeAmount < 0) {
      errors.supplierRestockingFeeAmount = 'Supplier restocking fee amount cannot be negative.';
    }

    if (this.freightRequired === true) {
      if (this.freightChargeMode !== CreditRequestFreightChargeMode.Deduct &&
          this.freightChargeMode !== CreditRequestFreightChargeMode.Separate) {
        errors.freightChargeMode = 'Select a freight charge option.';
      }
      requiredText(
        this.freightMojoeNumber,
        'freightMojoeNumber',
        'Freight Mojoe number is required.',
      );
    } else if (this.freightRequired === false) {
      requiredText(this.noFreightReason, 'noFreightReason', 'No freight reason is required.');
    }

    if (this.returnRequired) {
      requiredText(
        this.pickupAddress?.formattedAddress,
        'pickupAddress',
        'Pickup address is required.',
      );
      requiredText(
        this.destinationAddress?.formattedAddress,
        'destinationAddress',
        'Return address is required.',
      );
    }

    if (this.supplierContacted === true) {
      requiredText(this.supplierName, 'supplierName', 'Supplier name is required.');
      requiredText(
        this.supplierContactName,
        'supplierContactName',
        'Supplier contact name is required.',
      );
    }

    if (this.newPurchaseOrderRequired === true) {
      requiredAnswer(this.newPurchaseOrderNoCharge, 'newPurchaseOrderNoCharge');
    }

    const productReturnValue = hasInvalidReturnQuantity
      ? 0
      : round(this.productLines.reduce(
        (total, line) => total + line.returnQuantity * line.unitPrice,
        0,
      ));
    const customerRestockingFee = hasInvalidReturnQuantity
      ? 0
      : calculateTotalRestockingFee(this.productLines.map(line =>
        calculateProductRestockingFee(
          line.returnQuantity * line.unitPrice,
          line.restockingFeeApplied,
          line.restockingFeePercentage,
        )));
    const supplierRestockingFee = this.supplierRestockingFeeRequired === true
      ? Math.max(0, this.supplierRestockingFeeAmount)
      : 0;
    const deductedFreight = this.freightRequired === true &&
      this.freightChargeMode === CreditRequestFreightChargeMode.Deduct
      ? Math.max(0, this.freightAmount)
      : 0;
    const calculatedCreditExGst = hasInvalidReturnQuantity
      ? 0
      : calculateCustomerCredit(
        productReturnValue,
        customerRestockingFee + supplierRestockingFee,
        deductedFreight,
      );
    const totalCreditExGst = this.fixedAmountCreditExGst !== 0
      ? round(this.fixedAmountCreditExGst)
      : calculatedCreditExGst;
    const totalCreditIncludingGst = calculateCustomerCreditIncludingGst(totalCreditExGst);

    if (this.isCashCustomer && totalCreditIncludingGst > Math.max(0, this.amountPaid)) {
      errors.totalCreditIncludingGst = 'Cannot credit more than the amount paid';
    }

    this.errors = errors;
    return Object.keys(errors).length === 0 &&
      this.productLines.every(line => Object.keys(line.errors).length === 0);
  }

  public toSaveRequest(): ICreditRequestSaveRequest {
    return {
      rowVersion: this.rowVersion,
      reason: this.reason,
      customerCreditRequired: this.customerCreditRequired,
      returnRequired: this.returnRequired === true,
      pickupAddress: copyAddress(this.pickupAddress),
      destinationAddress: copyAddress(this.destinationAddress),
      freightRequired: this.freightRequired,
      freightChargeMode: this.freightChargeMode,
      freightAmount: this.freightAmount,
      freightMojoeNumber: this.freightMojoeNumber,
      noFreightReason: this.noFreightReason,
      newPurchaseOrderRequired: this.newPurchaseOrderRequired,
      newPurchaseOrderNoCharge: this.newPurchaseOrderNoCharge,
      supplierContacted: this.supplierContacted,
      supplierName: this.supplierName,
      supplierContactName: this.supplierContactName,
      supplierCreditRequired: this.supplierCreditRequired,
      supplierCreditAmount: this.supplierCreditAmount,
      supplierRestockingFeeRequired: this.supplierRestockingFeeRequired,
      supplierRestockingFeeAmount: this.supplierRestockingFeeAmount,
      fixedAmountCreditExGst: this.fixedAmountCreditExGst,
      generalComment: this.generalComment,
      cashCustomerAccountCheckedVerbally: this.cashCustomerAccountCheckedVerbally,
      cashCustomerAccountCheckedBy: this.cashCustomerAccountCheckedBy,
      productLines: this.productLines.map(line => line.toRequest()),
    };
  }
}

export const creditRequestStatusLabel = (status: CreditRequestStatus): string => {
  switch (status) {
    case CreditRequestStatus.AwaitingGeneralManager:
      return 'Awaiting General Manager';
    case CreditRequestStatus.AwaitingAccountsReceivable:
      return 'Awaiting Accounts Receivable';
    default:
      return status;
  }
};
